import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, ArrowLeft, Image as ImageIcon, ArrowRight, Camera, RectangleHorizontal } from 'lucide-react';

const polaroidOptions = [
    {
        id: 'polaroid-mini',
        type: 'polaroid',
        label: 'Mini (2x3")',
        icon: Camera,
        // Dimensions in mm - Defined as LANDSCAPE (Wide) internally so Portrait logic works
        designWidth: 76.2,  // 3 inches
        designHeight: 50.8, // 2 inches
        badgeWidth: 76.2,
        badgeHeight: 50.8,
        designArea: 76.2, // For compatibility/fallback logic
        description: 'Mini Instax size: 2 x 3 inches',
        popular: true
    },
    {
        id: 'polaroid-wallet',
        type: 'polaroid',
        label: 'Standard (2.5x3.5")',
        icon: ImageIcon,
        designWidth: 88.9,  // 3.5 inches
        designHeight: 63.5, // 2.5 inches
        badgeWidth: 88.9,
        badgeHeight: 63.5,
        designArea: 88.9,
        description: 'Classic Wallet size: 2.5 x 3.5 inches',
        popular: true
    },
    {
        id: 'polaroid-large',
        type: 'polaroid',
        label: 'Large (3x4")',
        icon: ImageIcon,
        designWidth: 101.6,  // 4 inches
        designHeight: 76.2, // 3 inches
        badgeWidth: 101.6,
        badgeHeight: 76.2,
        designArea: 101.6,
        description: 'Large Format: 3 x 4 inches',
        popular: false
    }
];

export default function PolaroidSizeSelection() {
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState(null);
    const [orientation, setOrientation] = useState('portrait');

    const handleCreate = () => {
        if (!selectedSize) return;

        const badgeData = {
            id: selectedSize.id,
            type: selectedSize.type,
            label: selectedSize.label,
            designArea: selectedSize.designArea,
            badgeSize: selectedSize.badgeWidth, // Use width as primary size
            designWidth: selectedSize.designWidth,
            designHeight: selectedSize.designHeight,
            badgeWidth: selectedSize.badgeWidth,
            badgeHeight: selectedSize.badgeHeight,
            description: selectedSize.description
        };

        navigate('/editor', {
            state: {
                badge: badgeData,
                orientation: orientation
            }
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted">
            {/* Header */}
            <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm">
                <div className="container mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/')}
                            className="rounded-xl"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Home
                        </Button>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-playful">
                                <Camera className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold gradient-text">Polaroid Maker</h1>
                                <p className="text-xs text-muted-foreground">Vintage Photo Creator</p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <div className="container mx-auto px-4 py-12">
                <div className="max-w-5xl mx-auto space-y-8">
                    {/* Title */}
                    <div className="text-center space-y-3">
                        <h2 className="text-4xl font-bold">Choose Photo Size</h2>
                        <p className="text-lg text-muted-foreground">Select a size for your polaroid-style photos</p>
                    </div>

                    {/* Size Grid */}
                    <div className="grid md:grid-cols-3 gap-6">
                        {polaroidOptions.map((option) => {
                            const Icon = option.icon;
                            const isSelected = selectedSize?.id === option.id;

                            return (
                                <Card
                                    key={option.id}
                                    onClick={() => setSelectedSize(option)}
                                    className={`card-playful p-6 cursor-pointer transition-all duration-300 relative overflow-hidden ${isSelected
                                        ? 'border-2 border-primary bg-primary/5 shadow-playful scale-105'
                                        : 'border border-border hover:border-primary/50 hover:scale-102'
                                        }`}
                                >
                                    {option.popular && (
                                        <Badge className="absolute top-3 right-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white border-none">
                                            Popular
                                        </Badge>
                                    )}

                                    <div className="flex flex-col items-center text-center space-y-4">
                                        <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-500/20 to-rose-500/20 flex items-center justify-center transition-transform duration-300 ${isSelected ? 'scale-110' : ''
                                            }`}>
                                            <Icon className="w-10 h-10 text-pink-600" />
                                        </div>

                                        <div>
                                            <h3 className="font-bold text-lg mb-1">{option.label}</h3>
                                            <p className="text-sm text-muted-foreground">{option.description}</p>
                                        </div>

                                        {isSelected && (
                                            <div className="w-full pt-2">
                                                <Badge className="w-full justify-center bg-success text-success-foreground">
                                                    Selected
                                                </Badge>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            );
                        })}
                    </div>

                    {/* Orientation Selection */}
                    {selectedSize && (
                        <Card className="card-playful p-6 bg-gradient-to-br from-pink-500/5 to-rose-500/5 border-pink-500/20">
                            <h3 className="font-semibold mb-4 text-center">Paper Orientation</h3>
                            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                                <Button
                                    variant={orientation === 'portrait' ? 'default' : 'outline'}
                                    onClick={() => setOrientation('portrait')}
                                    className={`rounded-xl h-auto py-4 flex flex-col gap-2 ${orientation === 'portrait' ? 'bg-pink-600 hover:bg-pink-700' : ''}`}
                                >
                                    <RectangleHorizontal className="w-6 h-6 rotate-90" />
                                    <span>Portrait (Tall)</span>
                                </Button>
                                <Button
                                    variant={orientation === 'landscape' ? 'default' : 'outline'}
                                    onClick={() => setOrientation('landscape')}
                                    className={`rounded-xl h-auto py-4 flex flex-col gap-2 ${orientation === 'landscape' ? 'bg-pink-600 hover:bg-pink-700' : ''}`}
                                >
                                    <RectangleHorizontal className="w-6 h-6" />
                                    <span>Landscape (Wide)</span>
                                </Button>
                            </div>
                        </Card>
                    )}

                    {/* Create Button */}
                    <div className="flex justify-center pt-4">
                        <Button
                            onClick={handleCreate}
                            disabled={!selectedSize}
                            size="lg"
                            className="rounded-2xl text-lg px-12 py-7 bg-gradient-to-r from-pink-500 to-rose-500 hover:shadow-hover transition-all duration-300 hover:scale-105 text-white"
                        >
                            Create Photo Design
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
